import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { products } from "@/api/client";
import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { MultiSelect } from "@/components/MultiSelect";
import { PageHead } from "@/components/PageHead";
import { useKindParam } from "@/lib/hooks/useKindParam";
import { qk, useProductCompanies, useProducts } from "@/modules/products/data";
import { ProductSelect, ScopeBar } from "@/modules/products/ScopeBar";

/** Who may work with a PRODUCT — a page of its own (Dattatray, 2026-09-28).
 *
 *  *"Just like model visibility sub page, the product shall have a product
 *  visibility subpage."*
 *
 *  THE TWO ARE DELIBERATELY SEPARATE, and the symmetry he is asking for is
 *  the point: they answer different halves of one question and are edited in
 *  the same shape.
 *
 *    THIS PAGE   ENTITLEMENT — may this company use the product at all
 *                (`company_products`)
 *    Asset Catalog / Model Visibility
 *                REACH — which MODELS within it they may build with
 *                (`catalog_model_visibility`)
 *
 *  Collapsing them would lose something real: a company can hold BMS and
 *  still be kept off the ASIL-D model another customer commissioned. Keeping
 *  them apart but IDENTICAL in shape is what makes that legible — an operator
 *  who has set one knows how to set the other.
 *
 *  It used to live inside the Products drawer's config panel, beside the
 *  spec-field editor, where "who may use this" sat under a heading about
 *  what the product records. That is two unrelated decisions in one box.
 */
export function ProductVisibilityPage() {
  const [kind, setKind] = useKindParam();
  const mayManage = useSession().can(CAP.MANAGE_PRODUCTS);
  const all = useProducts().data?.products ?? [];
  const q = useProductCompanies(kind);
  const qc = useQueryClient();

  const product = all.find((p) => p.kind === kind);
  const companies = q.data?.companies ?? [];
  const holders = companies.filter((c) => c.holds_product).map((c) => c.company_id);

  const [ids, setIds] = useState<string[]>([]);
  // Reload whenever the product changes — including to nothing, so a cleared
  // picker cannot leave the previous product's companies on screen ready to
  // be saved onto the next one (D-84, one level down).
  useEffect(() => { setIds(holders); }, [kind, holders.join(",")]);

  const save = useMutation({
    mutationFn: () => products.setCompanies(kind, ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.companies(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
    },
  });

  const dirty = [...ids].sort().join() !== [...holders].sort().join();

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products"
        title="Product visibility"
        desc="Which companies hold a product — whether they may use it at all. Which MODELS within it they may build with is set per model, in Asset Catalog / Model Visibility."
      />

      <ScopeBar>
        <ProductSelect value={kind} onChange={(k) => setKind(k)} />
      </ScopeBar>

      {!product ? (
        <div className="panel panel-pad cap">
          Choose a product to set who may work with it.
        </div>
      ) : (
        <div className="panel panel-pad">
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
            <span style={{ fontWeight: 700 }}>{product.name}</span>
            <span className="mono cap">{product.kind}</span>
            {ids.length === 0
              ? <Chip tone="mute">nobody yet</Chip>
              : <Chip tone="ok">{ids.length} compan{ids.length === 1 ? "y" : "ies"}</Chip>}
          </div>

          <div className="cap" style={{ marginBottom: 8, maxWidth: "72ch" }}>
            Holding a product lets a company use it at all. Unlike model
            visibility, empty here means <b>nobody</b> — a product nobody holds
            is one nobody can build with.
          </div>

          <div style={{ maxWidth: 460 }}>
            <MultiSelect
              options={companies.map((c) => ({ value: c.company_id, label: `${c.name} · ${c.company_id}` }))}
              value={ids} onChange={setIds} disabled={!mayManage}
              emptyLabel="Nobody yet" noneLabel="no organisations yet"
              clearLabel="Clear — nobody" />
          </div>

          {save.isError && (
            <div className="panel panel-pad" style={{
              marginTop: 12, borderColor: "var(--bad)",
              background: "color-mix(in srgb, var(--bad) 8%, transparent)",
              color: "var(--bad)",
            }}>
              <strong>Not saved.</strong> {(save.error as Error).message}
            </div>
          )}

          {mayManage && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
              <button className="btn teal sm" disabled={!dirty || save.isPending}
                onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
              {dirty && (
                <button className="btn ghost sm" onClick={() => setIds(holders)}>
                  Discard
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
